import dayjs from "dayjs";
import { isDateInRange } from "./serviceWindow";

export const VISIT_COMPLIANCE_STATUS = {
	COMPLETED: "Completed on time",
	LATE: "Late",
	EARLY: "Early",
	MISSED: "Missed",
	PENDING: "Pending",
	UPCOMING: "Upcoming",
	NO_WINDOW: "No service window",
};

export function getVisitTimelinessStatus(visitedDate, startDate, endDate) {
	if (!startDate || !endDate) {
		return VISIT_COMPLIANCE_STATUS.NO_WINDOW;
	}

	const today = dayjs().startOf("day");
	const windowStart = dayjs(startDate).startOf("day");
	const windowEnd = dayjs(endDate).startOf("day");

	if (!visitedDate) {
		if (today.isAfter(windowEnd)) return VISIT_COMPLIANCE_STATUS.MISSED;
		if (today.isBefore(windowStart)) return VISIT_COMPLIANCE_STATUS.UPCOMING;
		return VISIT_COMPLIANCE_STATUS.PENDING;
	}

	const visitDay = dayjs(visitedDate).startOf("day");
	if (visitDay.isAfter(windowEnd)) return VISIT_COMPLIANCE_STATUS.LATE;
	if (visitDay.isBefore(windowStart)) return VISIT_COMPLIANCE_STATUS.EARLY;
	return VISIT_COMPLIANCE_STATUS.COMPLETED;
}

export function isSuccessfulVisit(status) {
	return (
		status === VISIT_COMPLIANCE_STATUS.COMPLETED ||
		status === VISIT_COMPLIANCE_STATUS.EARLY
	);
}

function buildVisitLookup(visits = []) {
	const lookup = new Map();

	visits.forEach((visit) => {
		if (!visit.school_id || !visit.visit_type_id) return;

		const fmeId = visit.visited_by || visit.planned_by;
		if (!fmeId) return;

		const key = `${fmeId}:${visit.school_id}:${visit.visit_type_id}`;
		const existing = lookup.get(key);
		const candidateDate = visit.visited_date || visit.planned_date;

		if (!existing) {
			lookup.set(key, visit);
			return;
		}

		const existingDate = existing.visited_date || existing.planned_date;
		if (
			candidateDate &&
			(!existingDate || dayjs(candidateDate).isAfter(dayjs(existingDate)))
		) {
			lookup.set(key, visit);
		}
	});

	return lookup;
}

export function buildFmePerformanceMetrics({
	employees = [],
	assignments = [],
	serviceWindows = [],
	visits = [],
}) {
	const visitLookup = buildVisitLookup(visits);
	const rows = [];

	employees.forEach((employee) => {
		const employeeAssignments = assignments.filter(
			(row) => row.profile_id === employee.id && row.schools
		);

		serviceWindows.forEach((window) => {
			employeeAssignments.forEach((assignment) => {
				const school = assignment.schools;
				const key = `${employee.id}:${school.id}:${window.id}`;
				const matchedVisit = visitLookup.get(key);
				const visitedDate = matchedVisit?.visited_date || null;
				const plannedDate = matchedVisit?.planned_date || null;
				const status = getVisitTimelinessStatus(
					visitedDate,
					window.start_date,
					window.end_date
				);

				rows.push({
					id: `${employee.id}-${school.id}-${window.id}`,
					fmeId: employee.id,
					fmeName: employee.full_name || employee.email || "Unknown",
					fmeEmail: employee.email || "",
					schoolId: school.id,
					schoolName: school.name,
					schoolDistrict: school.district || "",
					visitTypeId: window.id,
					visitTypeName: window.name,
					windowStart: window.start_date,
					windowEnd: window.end_date,
					visitedDate,
					plannedDate,
					visitId: matchedVisit?.id || null,
					status,
					isSuccessful: isSuccessfulVisit(status),
				});
			});
		});
	});

	const summaryMap = new Map();

	rows.forEach((row) => {
		if (!summaryMap.has(row.fmeId)) {
			summaryMap.set(row.fmeId, {
				fmeId: row.fmeId,
				fmeName: row.fmeName,
				fmeEmail: row.fmeEmail,
				assignedSchools: new Set(),
				expectedVisits: 0,
				completedOnTime: 0,
				early: 0,
				late: 0,
				missed: 0,
				pending: 0,
				upcoming: 0,
			});
		}

		const summary = summaryMap.get(row.fmeId);
		summary.assignedSchools.add(row.schoolId);
		summary.expectedVisits += 1;

		switch (row.status) {
			case VISIT_COMPLIANCE_STATUS.COMPLETED:
				summary.completedOnTime += 1;
				break;
			case VISIT_COMPLIANCE_STATUS.EARLY:
				summary.early += 1;
				break;
			case VISIT_COMPLIANCE_STATUS.LATE:
				summary.late += 1;
				break;
			case VISIT_COMPLIANCE_STATUS.MISSED:
				summary.missed += 1;
				break;
			case VISIT_COMPLIANCE_STATUS.PENDING:
				summary.pending += 1;
				break;
			case VISIT_COMPLIANCE_STATUS.UPCOMING:
				summary.upcoming += 1;
				break;
			default:
				break;
		}
	});

	const summaries = Array.from(summaryMap.values())
		.map((summary) => {
			const successful =
				summary.completedOnTime + summary.early;
			const complianceRate =
				summary.expectedVisits > 0
					? Math.round((successful / summary.expectedVisits) * 100)
					: 0;

			return {
				...summary,
				assignedSchools: summary.assignedSchools.size,
				successfulVisits: successful,
				complianceRate,
			};
		})
		.sort((a, b) => a.fmeName.localeCompare(b.fmeName));

	const totals = summaries.reduce(
		(acc, summary) => {
			acc.expectedVisits += summary.expectedVisits;
			acc.successfulVisits += summary.successfulVisits;
			acc.completedOnTime += summary.completedOnTime;
			acc.early += summary.early;
			acc.late += summary.late;
			acc.missed += summary.missed;
			acc.pending += summary.pending;
			acc.upcoming += summary.upcoming;
			return acc;
		},
		{
			expectedVisits: 0,
			successfulVisits: 0,
			completedOnTime: 0,
			early: 0,
			late: 0,
			missed: 0,
			pending: 0,
			upcoming: 0,
		}
	);

	return {
		rows: rows.sort((a, b) => {
			const nameCompare = a.fmeName.localeCompare(b.fmeName);
			if (nameCompare !== 0) return nameCompare;
			const windowCompare = a.visitTypeName.localeCompare(b.visitTypeName);
			if (windowCompare !== 0) return windowCompare;
			return a.schoolName.localeCompare(b.schoolName);
		}),
		summaries,
		totals: {
			...totals,
			complianceRate:
				totals.expectedVisits > 0
					? Math.round((totals.successfulVisits / totals.expectedVisits) * 100)
					: 0,
			fmeCount: summaries.length,
		},
	};
}

export function buildServiceScheduleSummary(rows = []) {
	const scheduleMap = new Map();

	rows.forEach((row) => {
		if (!scheduleMap.has(row.visitTypeId)) {
			scheduleMap.set(row.visitTypeId, {
				visitTypeId: row.visitTypeId,
				visitTypeName: row.visitTypeName,
				completed: 0,
				scheduled: 0,
				toBeScheduled: 0,
			});
		}

		const entry = scheduleMap.get(row.visitTypeId);

		if (row.visitedDate) {
			entry.completed += 1;
		} else if (row.plannedDate) {
			entry.scheduled += 1;
		} else {
			entry.toBeScheduled += 1;
		}
	});

	return Array.from(scheduleMap.values()).sort((a, b) =>
		a.visitTypeName.localeCompare(b.visitTypeName)
	);
}

export function bucketSchoolProgress(averageProgress) {
	if (averageProgress === 100) return "exact100";
	if (averageProgress > 90) return "above90";
	if (averageProgress > 80) return "above80";
	if (averageProgress > 70) return "above70";
	if (averageProgress < 60) return "below60";
	return null;
}

export function buildSchoolProgressBuckets(schools = [], teachers = [], courseProgress = []) {
	const progressByTeacher = new Map();

	courseProgress.forEach((row) => {
		if (!progressByTeacher.has(row.teacher_id)) {
			progressByTeacher.set(row.teacher_id, []);
		}
		progressByTeacher.get(row.teacher_id).push(row);
	});

	const buckets = {
		exact100: 0,
		above90: 0,
		above80: 0,
		above70: 0,
		below60: 0,
		totalSchools: schools.length,
	};

	schools.forEach((school) => {
		const schoolTeachers = teachers.filter(
			(teacher) => teacher.school_id === school.id
		);

		if (schoolTeachers.length === 0) return;

		const teacherAverages = schoolTeachers.map((teacher) => {
			const progress = progressByTeacher.get(teacher.id) || [];
			if (progress.length === 0) return 0;
			return Math.round(
				progress.reduce(
					(sum, item) => sum + (item.completion_percentage || 0),
					0
				) / progress.length
			);
		});

		const schoolAverage = Math.round(
			teacherAverages.reduce((sum, value) => sum + value, 0) /
				teacherAverages.length
		);

		const bucket = bucketSchoolProgress(schoolAverage);
		if (bucket) buckets[bucket] += 1;
	});

	return buckets;
}

export function filterMetricsRows(rows, { search = "", fmeId = "all", visitTypeId = "all", status = "all" }) {
	return rows.filter((row) => {
		if (fmeId !== "all" && row.fmeId !== fmeId) return false;
		if (visitTypeId !== "all" && row.visitTypeId !== visitTypeId) return false;
		if (status !== "all" && row.status !== status) return false;

		if (!search.trim()) return true;

		const query = search.trim().toLowerCase();
		return [
			row.fmeName,
			row.fmeEmail,
			row.schoolName,
			row.schoolDistrict,
			row.visitTypeName,
			row.status,
		].some((value) => value?.toLowerCase().includes(query));
	});
}

export function serviceWindowOverlapsAcademicYear(window, academicYearRange) {
	if (!window?.start_date || !window?.end_date || !academicYearRange) {
		return false;
	}

	const windowStart = dayjs(window.start_date).startOf("day");
	const windowEnd = dayjs(window.end_date).endOf("day");

	return (
		!windowEnd.isBefore(academicYearRange.start) &&
		!windowStart.isAfter(academicYearRange.end)
	);
}
